import { Card, TextField } from '@mui/material';
import lodash from 'lodash';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { useGetLogs } from '../../hooks/query/logging/loggingQueries';
import { ILog } from '../../@types/modelV2/logging/loggingModels';
import Screen from '../customComponentV2/screen/Screen';
import DateHelper from '../../services/helper/dateHelper';
import CustomTable from '../customComponentV2/DataGrid/CustomTable';
import ProjectDialog from '../customComponentV2/dialog/ProjectDialog';
import { booleanToYesNo } from '../../services/helper/functionHelper';
import DateFilter from '../customComponentV2/Date/DateFilter';

const LogManager = () : React.ReactElement => {
    const [selectedLog, setSelectedLog] = useState<ILog | null>(null);

    const [selectedFromDateState, setSelectedFromDateState] = useState<moment.Moment>(moment());
    const [selectedToDateState, setSelectedToDateState] = useState<moment.Moment>(moment());

    const { isFetching: isLoadingLogs, data: logs, refetch: refetchLogs } = useGetLogs(getDate(selectedFromDateState, 'from'), getDate(selectedToDateState, 'to'));

    function getDate (date : moment.Moment, type : 'from' | 'to') : number {
        const selectedDate = {...date};
        switch (type) {
            case 'from':
                return moment(selectedDate).startOf('day').utc().unix() * 1000;
            case 'to':
                return moment(selectedDate).endOf('day').utc().unix() * 1000;
        }
    }

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openStackTraceDialog = (selectedLog : ILog) : void => {
        const log = logs?.find(x => x.id === selectedLog.id) || null;
        setSelectedLog(log);
    };

    const closeStackTraceDialog = () : void => {
        setSelectedLog(null);
    };

    const handleDateRangeChange = (start : moment.Moment | null, end : moment.Moment | null, changedBy ?: 'start' | 'end') : void => {
        const selectedFromDateV2 = changedBy === 'start' ?
            moment(start).startOf('day')
            : !end < !start ?
                moment(end).startOf('day')
                : moment(start).startOf('day');

        const selectedToDateV2 = changedBy === 'end' ?
            moment(end).endOf('day')
            : !start > !end ?
                moment(start).endOf('day')
                : moment(end).endOf('day');

        setSelectedFromDateState(selectedFromDateV2);
        setSelectedToDateState(selectedToDateV2);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLoadingLogs;
    }, [
        isLoadingLogs,
    ]);

    /*================================================================================================================
     *                                                  Render
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className={'fdc hfill'}>
                <div className={'fdr aic jcc pb20 pl20 pr20'}>
                    <div className={'flx1'}/>
                    <DateFilter
                        className={'ml10 mr10'}
                        selectedFromDate={selectedFromDateState}
                        selectedToDate={selectedToDateState}
                        handleDateRangeChange={handleDateRangeChange}
                        onApplyClick={refetchLogs} />
                </div>
                <Card className={'fdc flx1'}>
                    <CustomTable<ILog>
                        enableDetails
                        detailFunction={openStackTraceDialog}
                        enableRefresh
                        refreshFunction={refetchLogs}
                        enableSorting
                        enableFiltering
                        fitWidthToPage
                        enablePagination
                        columns={[
                            { title: 'Message', field: 'message', enableFiltering: true, enableSorting: true },
                            { title: 'Exception Message', field: 'exceptionMessage', enableFiltering: true, enableSorting: true },
                            { title: 'Inner Exception Message', field: 'innerExceptionMessage', enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', formatFunction: (value : string) => DateHelper.formatDateUtcToLocal(value, 'dateTime'), type: 'date', enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', formatFunction: (value : string) => DateHelper.formatDateUtcToLocal(value, 'dateTime'), type: 'date', enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', formatFunction: booleanToYesNo, type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={lodash.map(logs, x => x)}
                        initialSortOrder={[{ columnName: 'createdOn_Created On', direction: 'desc' }]}
                        isActive={(row : ILog) : boolean => row.isActive}
                    />
                </Card>
                <ProjectDialog
                    title={'Error Log Stack Trace'}
                    maxWidth={'lg'}
                    isLoadingCircular={isLoading}
                    isOpen={!!selectedLog}
                    onClose={closeStackTraceDialog}>
                    <div className={'hfill p10 oys'}>
                        <TextField
                            value={selectedLog?.stackTrace ?? ''}
                            multiline
                            variant={'outlined'}
                            disabled
                            className={'mnw800'}
                        />
                    </div>
                </ProjectDialog >
            </div>
        </Screen>
    );
};

export default LogManager;
